/**
 * A re-imported chapter merged into the book's (decision 64, L8, L9; part 23b, principles 3–5, tables MG, NT): the file's
 * reading against its reference, the pinned export snapshot (the base) or, with none, the current text. The reference's
 * own export, read back block by block (`referenceUnits`, exported and read by the caller), is what the file shows of
 * each of its blocks, so a file block equal to it is the reference's block, written from source; with a base the merge
 * is three-way over the base's blocks (diff3), the current text's changes kept where the file left the base alone, the
 * file's taken where both changed (a conflict: the caller keeps the current text as a conflict copy). Pure.
 */
import { chapterModel } from '../../../literal/model.ts';
import type { Block, ChapterModel, Footnote } from '../../../literal/model.ts';
import { outputLimit, serializeChapter } from '../../../literal/serialize.ts';
import { visibleBlocks } from '../../../literal/visible.ts';
import { parseChapter } from '../../../parser/parse.ts';
import { align } from './align.ts';
import { assemble, outermostDefinitions } from './assemble.ts';
import type { Placed } from './assemble.ts';
import { citedIn, KeyTable, noteDefinitions, SourceKeys } from './keys.ts';
import { footnoteKey } from '../../../parser/footnote-index.ts';

/**
 * KY4, PI6: the destination every picture the file doesn't hold is given, in the file's reading and in the reference's
 * alike, so two such pictures compare as one; it names no image (the placeholder's shape, part 18's G5, all zeros).
 */
export const UNHELD_PICTURE = `image:${'0'.repeat(32)}?sha256=${'0'.repeat(64)}`;

/** CO: the most lines a chapter's diff gives; the rest are counted. */
export const MAX_DIFF_LINES = 2_000;

/** CO: the most characters of a block a diff line gives; a longer one is cut, with an ellipsis. */
export const MAX_DIFF_TEXT = 2_000;

/** What the merge is given for one chapter. */
export interface MergeInput {
  /** The pinned export snapshot's text (CS2–CS4), or null when there is none (CS5). */
  readonly base: string | null;
  /** The chapter's stored text. */
  readonly current: string;
  /** The file's reading of the chapter (part 23a), each picture settled as part 23b's table PI says. */
  readonly file: string;
  /**
   * What each of the reference's units ({@link referenceUnits} of the base, else of the current text) reads back as; null
   * for a Markdown file (part 24d), which holds everything a source does, so each unit is its own reading, keyed where it
   * stands in the reference.
   */
  readonly readings: readonly string[] | null;
}

/** One line of the review's diff: a block of the chapter the merge removes or adds, as the writer sees its text. */
export interface DiffLine {
  readonly change: 'removed' | 'added';
  readonly text: string;
}

/** The merge's outcome for one chapter (CS1–CS5). */
export interface MergedChapter {
  /** The merged source: the current text when nothing visible changes (MG7). */
  readonly text: string;
  /** Whether `text` says something the current text doesn't (MG7). */
  readonly changed: boolean;
  /** Whether the file says something its reference doesn't (CS1, CS3: no). */
  readonly fileChanged: boolean;
  /** Regions both the file and the app changed, the file's taken (MG4). */
  readonly conflicts: number;
  /** The blocks removed and added, current to merged, in order, at most {@link MAX_DIFF_LINES}. */
  readonly diff: readonly DiffLine[];
  /** How many more lines the diff has. */
  readonly more: number;
}

/** A chapter read for the merge: its model, notes, keys and units (its top-level blocks but its notes' definitions). */
class Read {
  readonly blocks: readonly Block[];
  readonly notes: ReadonlyMap<string, Footnote>;
  readonly keys: SourceKeys;
  readonly units: readonly Block[];
  readonly unitKeys: readonly number[];

  constructor(text: string, table: KeyTable) {
    const model = chapterModel(parseChapter(text));
    this.blocks = model.blocks;
    this.notes = noteDefinitions(model);
    this.keys = new SourceKeys(this.notes, table);
    this.units = model.blocks.filter((block) => block.kind !== 'footnote');
    this.unitKeys = this.units.map((block) => this.keys.key(block));
  }

  /** The unit as a chapter of its own: the block, then the notes it reaches that it doesn't hold (NT5). */
  alone(unit: Block): ChapterModel {
    return assemble([this], [{ source: this, block: unit }]);
  }
}

/** Each top-level block of `source` as a chapter of its own, with the notes it cites (principle 3): what to export and read back. */
export function referenceUnits(source: string): string[] {
  const read = new Read(source, new KeyTable());
  return read.units.map((unit) => serializeChapter(read.alone(unit)));
}

/** A diff line's text: what the writer sees of the block and its notes, lines joined, cut at {@link MAX_DIFF_TEXT}. */
function shown(read: Read, unit: Block): string {
  const text = visibleBlocks(parseChapter(serializeChapter(read.alone(unit))))
    .map((block) => block.text)
    .join('\n');
  return text.length > MAX_DIFF_TEXT ? `${text.slice(0, MAX_DIFF_TEXT - 1)}…` : text;
}

/** The review's diff (V′5): `from`'s units against `to`'s, removed before added in each changed stretch. */
function diffOf(from: Read, to: Read): { readonly diff: DiffLine[]; readonly more: number } {
  const matched = align(from.unitKeys, to.unitKeys);
  const lines: { readonly change: 'removed' | 'added'; readonly read: Read; readonly unit: Block }[] = [];
  let next = 0;
  const added = (until: number): void => {
    for (const unit of to.units.slice(next, until)) lines.push({ change: 'added', read: to, unit });
  };
  from.units.forEach((unit, i) => {
    const at = Number(matched[i]);
    if (at === -1) lines.push({ change: 'removed', read: from, unit });
    else {
      added(at);
      next = at + 1;
    }
  });
  added(to.units.length);
  return {
    diff: lines.slice(0, MAX_DIFF_LINES).map((line) => ({ change: line.change, text: shown(line.read, line.unit) })),
    more: Math.max(0, lines.length - MAX_DIFF_LINES),
  };
}

/**
 * NT7 (deep-fix r1), NT8 (deep-fix r3): the current text's definitions that read as nothing, that no block placed holds.
 * The file can't hold them (part 22's C″10b), so its editor can't have taken them out, and the merge would otherwise drop
 * them. NT7: a note nothing in it cites (a draft note, a note whose mark was taken out) goes in after the blocks, as a
 * note. NT8: a label's later definition (a second `[^1]:`; only the first is the note, cited or not) goes in after every
 * note, as written, so it is still a later one wherever its label is written and shows nothing.
 */
function unread(current: Read, placed: readonly Placed[]): { readonly notes: Placed[]; readonly later: Footnote[] } {
  const held = new Set(placed.map(({ block }) => block));
  const cited = new Set(current.blocks.flatMap((block) => citedIn(block)).map(footnoteKey));
  const notes: Placed[] = [];
  const later: Footnote[] = [];
  for (const definition of outermostDefinitions(current.blocks.filter((block) => !held.has(block)))) {
    const key = footnoteKey(definition.label);
    if (current.notes.get(key) !== definition) later.push(definition);
    else if (!cited.has(key)) notes.push({ source: current, block: definition });
  }
  return { notes, later };
}

/** Whether `a` and `b` read as the same blocks, one for one. */
function sameKeys(a: Read, b: Read): boolean {
  return a.unitKeys.length === b.unitKeys.length && a.unitKeys.every((key, i) => key === b.unitKeys[i]);
}

/** Table MG: `input`'s chapter merged. Throws a RangeError when the readings aren't one per unit of the reference. */
export function mergeChapter(input: MergeInput): MergedChapter {
  const table = new KeyTable();
  const current = new Read(input.current, table);
  const file = new Read(input.file, table);
  const reference = input.base === null ? current : new Read(input.base, table);
  if (input.readings !== null && input.readings.length !== reference.units.length) {
    throw new RangeError(`${String(input.readings.length)} readings for ${String(reference.units.length)} blocks`);
  }
  // A1: the readings, one after another, against the file's blocks; a unit is kept by the file when every one of its
  // readings is matched, next to each other (MG6); one that reads as nothing is kept where it stands (MG5). A Markdown
  // file's readings are the units themselves, keyed in the reference, never read again alone (part 24d: a unit read alone
  // can key otherwise than where it stands).
  const flat: number[] = input.readings === null ? [...reference.unitKeys] : [];
  const owner: number[] = input.readings === null ? reference.unitKeys.map((_, unit) => unit) : [];
  for (const [unit, reading] of (input.readings ?? []).entries()) {
    for (const key of new Read(reading, table).unitKeys) {
      flat.push(key);
      owner.push(unit);
    }
  }
  const inFile = align(flat, file.unitKeys);
  const units = reference.units.length;
  const first = new Int32Array(units).fill(-1);
  const last = new Int32Array(units).fill(-1);
  const keptByFile = new Array<boolean>(units).fill(true);
  owner.forEach((unit, at) => {
    const to = Number(inFile[at]);
    if (to === -1 || (last[unit] !== -1 && last[unit] !== to - 1)) keptByFile[unit] = false;
    if (first[unit] === -1) first[unit] = to;
    last[unit] = to;
  });
  // A2: the base's blocks against the current text's; with no base the current text is the reference, each block its own.
  const inCurrent = input.base === null ? Int32Array.from(reference.units, (_, i) => i) : align(reference.unitKeys, current.unitKeys);
  const placed: Placed[] = [];
  let atFile = 0;
  let atCurrent = 0;
  let conflicts = 0;
  let fileChanged = false;
  let region: number[] = [];
  const takeCurrent = (to: number): void => {
    for (const block of current.units.slice(atCurrent, to)) placed.push({ source: current, block });
    atCurrent = Math.max(atCurrent, to);
  };
  /** MG1–MG5: blocks of the reference `part` covers, up to the file's `fileTo` and the current text's `currentTo`. */
  const resolve = (part: readonly number[], fileTo: number, currentTo: number): void => {
    const keptReadings = part.reduce((sum, unit) => sum + (keptByFile[unit] === true && first[unit] !== -1 ? Number(last[unit]) - Number(first[unit]) + 1 : 0), 0);
    const fileKept = part.every((unit) => keptByFile[unit] === true) && keptReadings === fileTo - atFile;
    const currentKept = part.every((unit) => inCurrent[unit] !== -1) && part.length === currentTo - atCurrent;
    if (fileKept) takeCurrent(currentTo);
    else {
      fileChanged = true;
      if (!currentKept) conflicts++;
      // MG5: a block no reading shows, still in the current text, stays where it stood.
      for (const unit of part) {
        const block = current.units[Number(inCurrent[unit])];
        if (first[unit] === -1 && keptByFile[unit] === true && block !== undefined) placed.push({ source: current, block });
      }
      for (const block of file.units.slice(atFile, fileTo)) placed.push({ source: file, block });
      atCurrent = currentTo;
    }
    atFile = fileTo;
  };
  /** Whether the file keeps reference block `unit` and shows it: its readings are the file's blocks first[unit]…last[unit]. */
  const shownKept = (unit: number | undefined): unit is number => unit !== undefined && keptByFile[unit] === true && first[unit] !== -1;
  /** Whether reference block `unit` is one no reading shows (MG5): it has no place of its own in the file. */
  const unshown = (unit: number | undefined): boolean => unit !== undefined && first[unit] === -1 && keptByFile[unit] === true;
  /**
   * MG4b: where the file's blocks part at each edge `k` of the region (between region[k - 1] and region[k]): after a
   * block it keeps, or before one; a block it never shows sits where its neighbours part; the region's own edges part
   * where it starts and ends; null where the file changed the blocks on both sides.
   */
  const fileEdges = (fileTo: number): (number | null)[] => {
    const direct = (k: number): number | null => {
      const before = region[k - 1];
      const after = region[k];
      if (shownKept(before)) return Number(last[before]) + 1;
      return shownKept(after) ? Number(first[after]) : null;
    };
    // From the right, then from the left: a block never shown takes the edge beside it on the side it was reached from.
    const right: (number | null)[] = [];
    let carry: number | null = null;
    for (let k = region.length; k >= 0; k--) {
      carry = direct(k) ?? (k === region.length ? fileTo : unshown(region[k]) ? carry : null);
      right.push(carry);
    }
    right.reverse();
    const out: (number | null)[] = [];
    carry = null;
    for (const [k, fromRight] of right.entries()) {
      carry = direct(k) ?? (k === 0 ? atFile : unshown(region[k - 1]) ? carry : null);
      out.push(carry ?? fromRight);
    }
    return out;
  };
  /** Where the current text's blocks part at edge `k`: after a block it keeps, or before one; the region's edges where it starts and ends. */
  const currentEdge = (k: number, currentTo: number): number | null => {
    const before = region[k - 1];
    const after = region[k];
    if (before !== undefined && inCurrent[before] !== -1) return Number(inCurrent[before]) + 1;
    if (after !== undefined && inCurrent[after] !== -1) return Number(inCurrent[after]);
    return k === 0 ? atCurrent : k === region.length ? currentTo : null;
  };
  /**
   * MG4b: the region between two blocks both sides keep, cut wherever both sides say where its blocks part, so a block or
   * an insertion only one side made beside one only the other made is no conflict; each piece resolved alone.
   */
  const close = (fileTo: number, currentTo: number): void => {
    let from = 0;
    for (const [k, fileAt] of fileEdges(fileTo).entries()) {
      const currentAt = currentEdge(k, currentTo);
      if (fileAt === null || currentAt === null) continue;
      resolve(region.slice(from, k), fileAt, currentAt);
      from = k;
    }
    resolve(region.slice(from), fileTo, currentTo);
    region = [];
  };
  for (let unit = 0; unit < units; unit++) {
    const at = Number(inCurrent[unit]);
    // A block both sides keep, and the file shows, is an anchor; one no reading shows never is (MG5).
    if (keptByFile[unit] !== true || at === -1 || first[unit] === -1) {
      region.push(unit);
      continue;
    }
    close(Number(first[unit]), at);
    takeCurrent(at + 1);
    atFile = Number(last[unit]) + 1;
  }
  close(file.units.length, current.units.length);
  const { notes, later } = unread(current, placed);
  placed.push(...notes);
  // MG9: the canonical writer writes a model that reads back as itself (part 04), and table NT keeps every note's
  // citations, so the text reads back as the blocks it was made of (the merge's and NT's properties check it).
  const limit = outputLimit(input.current.length + input.file.length);
  const model = assemble([current, file], placed);
  let text = serializeChapter({ blocks: [...model.blocks, ...later] }, limit);
  let merged = new Read(text, table);
  if (later.length > 0 && !sameKeys(merged, new Read(serializeChapter(model, limit), table))) {
    // NT8: a later definition holding a label's first one (nested in it) would, as written, define a mark the file's
    // text writes as text; it goes in as NT7's do, its labels renumbered where they are taken, still showing nothing.
    text = serializeChapter(assemble([current, file], [...placed, ...later.map((block): Placed => ({ source: current, block }))]), limit);
    merged = new Read(text, table);
  }
  // MG7: nothing visible changed, so the current text stands as it is.
  const changed = !sameKeys(merged, current);
  return { text: changed ? text : input.current, changed, fileChanged, conflicts, ...diffOf(current, merged) };
}

/**
 * Part 24d, U5: three Markdown sources merged three ways (table MG) on `base`: a bundle update's chapter, `current` this
 * device's text and `file` the bundle's. A Markdown source's blocks are their own readings, keyed where they stand in the
 * base (no readings).
 */
export function mergeSources(base: string, current: string, file: string): MergedChapter {
  return mergeChapter({ base, current, file, readings: null });
}
